// src/pages/DashboardProject/CreateJob.tsx
import React, { useState, useEffect } from 'react';
import {
    Button, Card, CardHeader, CardBody, Col, Row, Alert, Nav, NavItem, NavLink, TabContent, TabPane
} from 'reactstrap';
import { Link, useNavigate } from 'react-router-dom';
import classnames from "classnames";
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import { ChatMessageProps } from './ChatMessage';
import { Address, formatAddress } from '../../models/Address.interface';
import CategoryBadges from './CategoryBadges';
import { useUser } from '../../hooks';
import { Attachment } from '../../models/Attachment.interface';
import { CreateJobFormSteps } from './CreateJobFormSteps';
import { saveInitialJobToFirebase, updateJobInFirebase } from './JobSubmissionService';
import ContractorMatchingStatus from './ContractorMatchingStatus';
import LoadingOverlay from '../../Components/LoadingOverlay';
import './styles.css';

interface CreateJobProps {
    onJobCreated?: (jobId: string) => void;
    showHeader?: boolean;
    redirectOnSuccess?: boolean;
}

const CreateJob: React.FC<CreateJobProps> = ({
    onJobCreated,
    showHeader = true,
    redirectOnSuccess = true
}) => {
    const navigate = useNavigate();
    const { currentUserId, currentUser, userName } = useUser();

    // Get reCAPTCHA v3 hook
    const { executeRecaptcha } = useGoogleReCaptcha();

    // Form state with localStorage persistence
    const [jobId, setJobId] = useState<string | null>(() =>
        localStorage.getItem('createJob_jobId')
    );
    const [jobDescription, setJobDescription] = useState(() =>
        localStorage.getItem('createJob_jobDescription') || ''
    );
    const [jobAttachments, setJobAttachments] = useState<File[]>([]);
    const [existingAttachments, setExistingAttachments] = useState<Attachment[]>(() => {
        const stored = localStorage.getItem('createJob_existingAttachments');
        return stored ? JSON.parse(stored) : [];
    });
    const [jobTitle, setJobTitle] = useState(() =>
        localStorage.getItem('createJob_jobTitle') || ''
    );
    const [jobSummary, setJobSummary] = useState(() =>
        localStorage.getItem('createJob_jobSummary') || ''
    );
    const [seoUrl, setSeoUrl] = useState(() =>
        localStorage.getItem('createJob_seoUrl') || ''
    );
    const [jobDetail, setJobDetail] = useState(() =>
        localStorage.getItem('createJob_jobDetail') || ''
    );
    const [jobCategories, setJobCategories] = useState<string[]>(() => {
        const stored = localStorage.getItem('createJob_jobCategories');
        return stored ? JSON.parse(stored) : [];
    });
    const [complete, setComplete] = useState(() =>
        localStorage.getItem('createJob_complete') === 'true'
    );
    const [chatMessages, setChatMessages] = useState<ChatMessageProps[]>(() => {
        const stored = localStorage.getItem('createJob_chatMessages');
        return stored ? JSON.parse(stored) : [];
    });
    const [jobStatus, setJobStatus] = useState(() =>
        localStorage.getItem('createJob_jobStatus') || 'Draft'
    );
    const [ownerName, setOwnerName] = useState(() =>
        localStorage.getItem('createJob_ownerName') || ''
    );
    const [ownerEmail, setOwnerEmail] = useState(() =>
        localStorage.getItem('createJob_ownerEmail') || ''
    );
    const [ownerPhone, setOwnerPhone] = useState(() =>
        localStorage.getItem('createJob_ownerPhone') || ''
    );
    const [contactPersonRole, setContactPersonRole] = useState(() =>
        localStorage.getItem('createJob_contactPersonRole') || ''
    );
    const [activeVerticalTab, setActiveVerticalTab] = useState<number>(() =>
        parseInt(localStorage.getItem('createJob_activeTab') || '7')
    );
    const [passedVerticalSteps, setPassedVerticalSteps] = useState<any>(() => {
        const stored = localStorage.getItem('createJob_passedSteps');
        return stored ? JSON.parse(stored) : [7];
    });

    const [propertyAddress, setPropertyAddress] = useState<Address>(() => {
        const stored = localStorage.getItem('createJob_propertyAddress');
        return stored ? JSON.parse(stored) : {
            address1: '',
            city: '',
            state: '',
            zipCode: ''
        };
    });

    // Separate loading states
    const [isInitialSaving, setIsInitialSaving] = useState(false);
    const [isFinalSubmitting, setIsFinalSubmitting] = useState(false);

    const [submissionResult, setSubmissionResult] = useState<{
        success: boolean;
        jobId?: string;
        matchedContractorCount?: number;
        error?: any;
    } | null>(null);
    const [isJobSubmitted, setIsJobSubmitted] = useState(() =>
        localStorage.getItem('createJob_isJobSubmitted') === 'true'
    );

    // Auto-save job when moving from step 1 to step 2
    const [hasInitialSave, setHasInitialSave] = useState(() =>
        localStorage.getItem('createJob_hasInitialSave') === 'true'
    );

    // reCAPTCHA error state
    const [recaptchaError, setRecaptchaError] = useState<string | null>(null);

    // Computed loading state - either initial saving or final submitting
    const isLoading = isInitialSaving || isFinalSubmitting;

    // Persist form data to localStorage
    useEffect(() => {
        localStorage.setItem('createJob_jobDescription', jobDescription);
        localStorage.setItem('createJob_jobTitle', jobTitle);
        localStorage.setItem('createJob_jobSummary', jobSummary);
        localStorage.setItem('createJob_seoUrl', seoUrl);
        localStorage.setItem('createJob_jobDetail', jobDetail);
        localStorage.setItem('createJob_jobCategories', JSON.stringify(jobCategories));
        localStorage.setItem('createJob_complete', complete.toString());
        localStorage.setItem('createJob_chatMessages', JSON.stringify(chatMessages));
        localStorage.setItem('createJob_jobStatus', jobStatus);
        localStorage.setItem('createJob_ownerName', ownerName);
        localStorage.setItem('createJob_ownerEmail', ownerEmail);
        localStorage.setItem('createJob_ownerPhone', ownerPhone);
        localStorage.setItem('createJob_contactPersonRole', contactPersonRole);
        localStorage.setItem('createJob_propertyAddress', JSON.stringify(propertyAddress));
        localStorage.setItem('createJob_existingAttachments', JSON.stringify(existingAttachments));
        localStorage.setItem('createJob_activeTab', activeVerticalTab.toString());
        localStorage.setItem('createJob_passedSteps', JSON.stringify(passedVerticalSteps));
        localStorage.setItem('createJob_hasInitialSave', hasInitialSave.toString());
        localStorage.setItem('createJob_isJobSubmitted', isJobSubmitted.toString());
        if (jobId) {
            localStorage.setItem('createJob_jobId', jobId);
        }
    }, [
        jobDescription, jobTitle, jobSummary, seoUrl, jobDetail, jobCategories,
        complete, chatMessages, jobStatus, ownerName, ownerEmail, ownerPhone,
        contactPersonRole, propertyAddress, existingAttachments,
        activeVerticalTab, passedVerticalSteps, hasInitialSave, isJobSubmitted, jobId
    ]);

    // Set up form data object for easy passing to components
    const formData = {
        jobDescription, setJobDescription,
        jobAttachments, setJobAttachments,
        existingAttachments, setExistingAttachments,
        jobTitle, setJobTitle,
        jobSummary, setJobSummary,
        seoUrl, setSeoUrl,
        jobDetail, setJobDetail,
        jobCategories, setJobCategories,
        complete, setComplete,
        chatMessages, setChatMessages,
        jobStatus, setJobStatus,
        ownerName, setOwnerName,
        ownerEmail, setOwnerEmail,
        ownerPhone, setOwnerPhone,
        contactPersonRole, setContactPersonRole,
        propertyAddress, setPropertyAddress
    };

    // Check for submitted job on component mount and handle page refresh
    useEffect(() => {
        const storedJobSubmitted = localStorage.getItem('createJob_isJobSubmitted') === 'true';
        const storedJobStatus = localStorage.getItem('createJob_jobStatus');

        // If job was submitted, clear localStorage and reset form
        if (storedJobSubmitted || storedJobStatus === 'Submitted') {
            console.log('Detected submitted job on page load - clearing localStorage and resetting form');
            clearFormData();
            resetFormToInitialState();
        }
    }, []); // Only run on mount

    // Initialize form with user data if logged in
    useEffect(() => {
        if (currentUser && !ownerName && !ownerEmail && !isJobSubmitted) {
            setOwnerName(userName);
            setOwnerEmail(currentUser.email || '');
        }
    }, [currentUser, userName, ownerName, ownerEmail, isJobSubmitted]);

    // Auto-save job when moving from step 1 to step 2
    useEffect(() => {
        const shouldAutoSave = !hasInitialSave &&
            activeVerticalTab >= 8 &&
            ownerName &&
            ownerEmail &&
            propertyAddress.address1;

        if (shouldAutoSave) {
            handleInitialSave();
        }
    }, [activeVerticalTab, hasInitialSave, ownerName, ownerEmail, propertyAddress.address1]);

    const handleInitialSave = async () => {
        if (hasInitialSave && jobId) return; // Already saved

        // Check if reCAPTCHA is available
        if (!executeRecaptcha) {
            setRecaptchaError('reCAPTCHA not available. Please refresh the page and try again.');
            return;
        }

        setIsInitialSaving(true);
        setRecaptchaError(null);

        try {
            // Execute reCAPTCHA v3 for initial save
            const recaptchaToken = await executeRecaptcha('job_initial_save');

            if (!recaptchaToken) {
                throw new Error('Failed to generate reCAPTCHA token for initial save');
            }

            console.log('reCAPTCHA v3 token generated successfully for initial save');

            const result = await saveInitialJobToFirebase({
                ownerName,
                ownerEmail,
                ownerPhone,
                contactPersonRole,
                propertyAddress,
                currentUserId: currentUserId || null,
                jobStatus: 'Draft',
                recaptchaToken
            });

            if (result.success && result.jobId) {
                setJobId(result.jobId);
                setHasInitialSave(true);
                console.log('Initial job saved with ID:', result.jobId);
            } else {
                throw new Error(result.error || 'Failed to save initial job');
            }
        } catch (error) {
            console.error('Error saving initial job:', error);
            const errorMessage = error instanceof Error ? error.message : 'Failed to save initial job';

            if (errorMessage.includes('reCAPTCHA') || errorMessage.includes('Security verification failed')) {
                setRecaptchaError(errorMessage);
            } else {
                setRecaptchaError('Failed to save job. Please try again.');
            }
        } finally {
            setIsInitialSaving(false);
        }
    };

    const handleJobUpdate = async (updateData: any) => {
        if (!jobId) return;

        // Check if reCAPTCHA is available
        if (!executeRecaptcha) {
            console.warn('reCAPTCHA not available for job update');
            return;
        }

        try {
            // Execute reCAPTCHA v3 for job update
            const recaptchaToken = await executeRecaptcha('job_update');

            if (!recaptchaToken) {
                console.warn('Failed to generate reCAPTCHA token for job update');
                return;
            }

            // Add recaptcha token to update data
            const updateDataWithRecaptcha = {
                ...updateData,
                recaptchaToken
            };

            await updateJobInFirebase(jobId, updateDataWithRecaptcha);
            console.log('Job updated successfully');
        } catch (error) {
            console.error('Error updating job:', error);
        }
    };

    // Update job when significant changes occur
    useEffect(() => {
        if (jobId && hasInitialSave) {
            const updateData: any = {};

            if (jobTitle) updateData.jobTitle = jobTitle;
            if (jobSummary) updateData.jobSummary = jobSummary;
            if (jobDetail) updateData.jobDetail = jobDetail;
            if (jobCategories.length > 0) updateData.jobCategories = jobCategories;
            if (chatMessages.length > 0) updateData.chatMessages = chatMessages;
            if (complete !== undefined) updateData.complete = complete;
            if (contactPersonRole) updateData.contactPersonRole = contactPersonRole;

            if (Object.keys(updateData).length > 0) {
                handleJobUpdate(updateData);
            }
        }
    }, [jobTitle, jobSummary, jobDetail, jobCategories, chatMessages, complete, contactPersonRole, jobId, hasInitialSave]);

    function toggleVerticalTab(tab: any) {
        if (activeVerticalTab !== tab) {
            var modifiedSteps = [...passedVerticalSteps, tab];

            if (tab >= 7 && tab <= 11) {
                setActiveVerticalTab(tab);
                setPassedVerticalSteps(modifiedSteps);
            }
        }
    }

    const handleSubmit = async () => {
        if (!jobId) {
            setSubmissionResult({
                success: false,
                error: 'No job ID found. Please refresh the page and try again.'
            });
            return;
        }

        setIsFinalSubmitting(true);
        setSubmissionResult(null);
        setRecaptchaError(null);

        try {
            // Execute reCAPTCHA v3 for final submission
            const recaptchaToken = executeRecaptcha ? await executeRecaptcha('job_submit') : null;

            if (!recaptchaToken) {
                throw new Error('Failed to generate reCAPTCHA token for job submission');
            }

            console.log('reCAPTCHA v3 token generated successfully for job submission');

            // Update the job with final data and mark as submitted
            const finalUpdateData = {
                jobTitle,
                jobDescription,
                jobSummary,
                seoUrl,
                jobDetail,
                jobCategories,
                propertyAddress,
                jobAttachments: existingAttachments, // Use existing attachments that were uploaded during the process
                complete: true,
                chatMessages,
                jobStatus: 'Submitted',
                ownerName,
                ownerEmail,
                ownerPhone,
                contactPersonRole,
                currentUserId: currentUserId || null,
                dtSubmitted: new Date().toISOString(),
                recaptchaToken
            };

            const result = await updateJobInFirebase(jobId, finalUpdateData, true); // true indicates final submission

            console.log('Job submission result:', result);

            // UPDATED: No longer wait for matchedContractorCount - it will be updated via real-time listener
            setSubmissionResult({
                success: result.success,
                jobId: jobId, // Use the jobId we already have, not from result
                matchedContractorCount: undefined, // Will be populated by ContractorMatchingStatus component
                error: result.error
            });

            if (result.success) {
                // Mark job as submitted
                setIsJobSubmitted(true);
                setJobStatus('Submitted');

                // Switch to success tab immediately (no waiting for matching)
                setActiveVerticalTab(11);

                // Call callback if provided
                if (onJobCreated) {
                    onJobCreated(jobId);
                }

                // Redirect if configured (user doesn't have to wait for matching)
                if (redirectOnSuccess) {
                    setTimeout(() => {
                        navigate(`/job-detail/${jobId}`);
                    }, 3000);
                }
            } else {
                throw new Error(result.error || 'Failed to submit job');
            }
        } catch (error) {
            console.error('Error submitting job:', error);
            const errorMessage = error instanceof Error
                ? error.message
                : 'An unexpected error occurred during job submission';

            setSubmissionResult({
                success: false,
                error: errorMessage
            });
        } finally {
            setIsFinalSubmitting(false); // Hide loading overlay immediately after job is submitted to Firebase
        }
    };

    const clearFormData = () => {
        // Clear all localStorage items
        const keys = [
            'createJob_jobId', 'createJob_jobDescription', 'createJob_jobTitle',
            'createJob_jobSummary', 'createJob_seoUrl', 'createJob_jobDetail',
            'createJob_jobCategories', 'createJob_complete', 'createJob_chatMessages',
            'createJob_jobStatus', 'createJob_ownerName', 'createJob_ownerEmail',
            'createJob_ownerPhone', 'createJob_contactPersonRole', 'createJob_propertyAddress',
            'createJob_existingAttachments', 'createJob_activeTab',
            'createJob_passedSteps', 'createJob_hasInitialSave', 'createJob_isJobSubmitted',
            'createJob_selectedAddress' // Add the new localStorage key
        ];

        keys.forEach(key => localStorage.removeItem(key));
    };


    const resetFormToInitialState = () => {
        // Reset all form state to initial values
        setJobId(null);
        setJobDescription('');
        setJobAttachments([]);
        setExistingAttachments([]);
        setJobTitle('');
        setJobSummary('');
        setSeoUrl('');
        setJobDetail('');
        setJobCategories([]);
        setComplete(false);
        setChatMessages([]);
        setJobStatus('Draft');
        setOwnerName('');
        setOwnerEmail('');
        setOwnerPhone('');
        setContactPersonRole('');
        setPropertyAddress({
            address1: '',
            city: '',
            state: '',
            zipCode: ''
        });
        setActiveVerticalTab(7);
        setPassedVerticalSteps([7]);
        setHasInitialSave(false);
        setIsJobSubmitted(false);
        setSubmissionResult(null);
        setRecaptchaError(null);
        setIsInitialSaving(false);
        setIsFinalSubmitting(false);
    };

    const handleStartNewJob = () => {
        console.log('Starting new job - clearing all data and resetting form');
        clearFormData();
        resetFormToInitialState();

        // If user is logged in, pre-populate their info
        if (currentUser && userName) {
            setOwnerName(userName);
            setOwnerEmail(currentUser.email || '');
        }
    };

    const isCompleteIcon = complete ? (
        <span className="text-success">
            <i className="ri-checkbox-circle-fill text-success fs-2"></i>
        </span>
    ) : (
        <span className="text-danger">
            <i className="ri-close-circle-fill text-danger fs-2"></i>
        </span>
    );

    const isCategoryIcon = jobCategories.length > 0 ? (
        <span className="text-success">
            <i className="ri-checkbox-circle-fill text-success fs-2"></i>
        </span>
    ) : (
        <span className="text-danger">
            <i className="ri-close-circle-fill text-danger fs-2"></i>
        </span>
    );

    const isAddressIcon = propertyAddress.address1 ? (
        <span className="text-success">
            <i className="ri-map-pin-fill text-success fs-2"></i>
        </span>
    ) : (
        <span className="text-danger">
            <i className="ri-map-pin-line text-danger fs-2"></i>
        </span>
    );

    const isFilesIcon = (jobAttachments.length > 0 || existingAttachments.length > 0) ? (
        <span className="text-success">
            <i className="ri-file-list-fill text-success fs-2"></i>
        </span>
    ) : (
        <span className="text-danger">
            <i className="ri-file-list-line text-danger fs-2"></i>
        </span>
    );

    // Determine LoadingOverlay text and subtext based on current state
    const getLoadingText = () => {
        if (isInitialSaving) {
            return "Saving Your Job";
        }
        if (isFinalSubmitting) {
            return "Saving Your Job";
        }
        return "Saving Your Job";
    };

    const getLoadingSubtext = () => {
        if (isInitialSaving) {
            return "Autosaving Draft Job";
        }
        if (isFinalSubmitting) {
            return "Matching qualified contractors in your area...";
        }
        return "Please wait...";
    };

    return (
        <div>
            <LoadingOverlay
                isVisible={isLoading}
                size="medium"
                text={getLoadingText()}
                subtext={getLoadingSubtext()}
                debug={false}
            />

            {/* Display reCAPTCHA errors */}
            {recaptchaError && (
                <Row className="mb-3">
                    <Col xs={12}>
                        <Alert color="danger">
                            <strong>Security Verification Error:</strong> {recaptchaError}
                            <br />
                            <small>Please refresh the page and try again. If the problem persists, please contact support.</small>
                        </Alert>
                    </Col>
                </Row>
            )}

            <Row>
                <Col xl={12}>
                    <Card>
                        {showHeader && (
                            <CardHeader>
                                <h4 className="card-title mb-0">Create Your Project</h4>
                            </CardHeader>
                        )}
                        <CardBody>
                            <form className="vertical-navs-step form-steps" autoComplete="off">
                                <Row className="gy-5">
                                    <Col lg={2}>
                                        <Nav className="flex-column custom-nav nav-pills">
                                            <NavItem>
                                                <NavLink
                                                    href="#"
                                                    className={classnames({
                                                        active: activeVerticalTab === 7,
                                                        done: (activeVerticalTab <= 11 && activeVerticalTab > 7),
                                                    })}
                                                    onClick={() => toggleVerticalTab(7)}
                                                >
                                                    <span className="step-title me-2">
                                                        <i className="ri-close-circle-fill step-icon me-2"></i>
                                                        Step 1
                                                    </span>
                                                    Contact & Address
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    href="#"
                                                    className={classnames({
                                                        active: activeVerticalTab === 8,
                                                        done: (activeVerticalTab <= 11 && activeVerticalTab >= 8),
                                                    })}
                                                    onClick={() => toggleVerticalTab(8)}
                                                >
                                                    <span className="step-title me-2">
                                                        <i className="ri-close-circle-fill step-icon me-2"></i>
                                                        Step 2
                                                    </span>
                                                    Job Details
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    href="#"
                                                    className={classnames({
                                                        active: activeVerticalTab === 9,
                                                        done: (activeVerticalTab <= 11 && activeVerticalTab >= 9),
                                                    })}
                                                    onClick={() => toggleVerticalTab(9)}
                                                >
                                                    <span className="step-title me-2">
                                                        <i className="ri-close-circle-fill step-icon me-2"></i>
                                                        Step 3
                                                    </span>
                                                    Images / Attachments
                                                </NavLink>
                                            </NavItem>
                                            <NavItem>
                                                <NavLink
                                                    href="#"
                                                    className={classnames({
                                                        active: activeVerticalTab === 10,
                                                        done: (activeVerticalTab <= 11 && activeVerticalTab >= 10),
                                                    })}
                                                    onClick={() => toggleVerticalTab(10)}
                                                >
                                                    <span className="step-title me-2">
                                                        <i className="ri-close-circle-fill step-icon me-2"></i>
                                                        Step 4
                                                    </span>
                                                    Confirm Details
                                                </NavLink>
                                            </NavItem>
                                            {submissionResult && (
                                                <NavItem>
                                                    <NavLink
                                                        href="#"
                                                        className={classnames({
                                                            active: activeVerticalTab === 11,
                                                        })}
                                                    >
                                                        <span className="step-title me-2">
                                                            <i className={`ri-${submissionResult.success ? 'checkbox-circle-fill text-success' : 'close-circle-fill text-danger'} step-icon me-2`}></i>
                                                            Step 5
                                                        </span>
                                                        {submissionResult.success ? 'Success!' : 'Error'}
                                                    </NavLink>
                                                </NavItem>
                                            )}
                                        </Nav>
                                    </Col>

                                    <Col lg={8}>
                                        <div className="px-lg-1">
                                            {activeVerticalTab !== 11 ? (
                                                <CreateJobFormSteps
                                                    formData={formData}
                                                    activeTab={activeVerticalTab}
                                                    onTabChange={toggleVerticalTab}
                                                    jobId={jobId}
                                                    onFileUpload={(attachments) => setExistingAttachments(prev => [...prev, ...attachments])}
                                                />
                                            ) : (
                                                // Success/Error Tab
                                                <TabContent activeTab={11}>
                                                    <TabPane tabId={11}>
                                                        <div className="text-center">
                                                            {submissionResult?.success ? (
                                                                <>
                                                                    <div className="mb-4">
                                                                        <i className="ri-checkbox-circle-fill text-success" style={{ fontSize: '4rem' }}></i>
                                                                    </div>
                                                                    <h3 className="text-success mb-3">Job Created Successfully! 🎉</h3>
                                                                    <Alert color="success" className="text-start">
                                                                        <strong>Great news!</strong> Your job "{jobTitle}" has been created and is ready for contractors to bid on.

                                                                        <ContractorMatchingStatus
                                                                            jobId={jobId || ''}
                                                                            initialMatchingStatus="matching"
                                                                            initialMatchedCount={0}
                                                                        />
                                                                    </Alert>
                                                                    <div className="mt-4">
                                                                        <h5>📋 Next Steps:</h5>
                                                                        <div className="text-start">
                                                                            <ol>
                                                                                <li><strong>Check Your Email:</strong> We've sent you a confirmation with all the details</li>
                                                                                {!currentUserId && (
                                                                                    <li><strong>Create Account:</strong> <Link to="/register">Sign up</Link> to manage your job and review bids</li>
                                                                                )}
                                                                                <li><strong>Review Your Job:</strong> Make sure all information is correct</li>
                                                                                <li><strong>Invite Contractors:</strong> Go to the "Hiring" tab to select and invite contractors</li>
                                                                                <li><strong>Review Bids:</strong> Compare proposals when contractors submit their bids</li>
                                                                                <li><strong>Start Your Project:</strong> Accept the best bid to begin work</li>
                                                                            </ol>
                                                                        </div>
                                                                    </div>
                                                                    {submissionResult.jobId && (
                                                                        <div className="mt-4">
                                                                            {currentUserId ? (
                                                                                <>
                                                                                    <Link
                                                                                        to={`/job-detail/${submissionResult.jobId}?tab=bids`}
                                                                                        className="btn btn-primary me-2"
                                                                                    >
                                                                                        <i className="ri-group-line me-1"></i>
                                                                                        Start Hiring Contractors
                                                                                    </Link>
                                                                                    <Link
                                                                                        to={`/job-detail/${submissionResult.jobId}`}
                                                                                        className="btn btn-secondary"
                                                                                    >
                                                                                        <i className="ri-eye-line me-1"></i>
                                                                                        View Job Details
                                                                                    </Link>
                                                                                </>
                                                                            ) : (
                                                                                <>
                                                                                    <Link
                                                                                        to="/login"
                                                                                        className="btn btn-primary me-2"
                                                                                    >
                                                                                        <i className="ri-user-add-line me-1"></i>
                                                                                        Login or Register to Manage Job
                                                                                    </Link>
                                                                                    <Link
                                                                                        to="/"
                                                                                        className="btn btn-secondary"
                                                                                    >
                                                                                        <i className="ri-home-line me-1"></i>
                                                                                        Return to Home
                                                                                    </Link>
                                                                                </>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                </>
                                                            ) : (
                                                                <>
                                                                    <div className="mb-4">
                                                                        <i className="ri-close-circle-fill text-danger" style={{ fontSize: '4rem' }}></i>
                                                                    </div>
                                                                    <h3 className="text-danger mb-3">Job Creation Failed</h3>
                                                                    <Alert color="danger">
                                                                        <strong>Sorry!</strong> There was an error creating your job. Please try again.
                                                                        {submissionResult?.error && (
                                                                            <div className="mt-2">
                                                                                <small>Error: {submissionResult.error.message || 'Unknown error'}</small>
                                                                            </div>
                                                                        )}
                                                                    </Alert>
                                                                    <Button
                                                                        color="primary"
                                                                        onClick={() => {
                                                                            setSubmissionResult(null);
                                                                            setActiveVerticalTab(10);
                                                                        }}
                                                                        className="me-2"
                                                                    >
                                                                        Try Again
                                                                    </Button>
                                                                    <Button
                                                                        color="secondary"
                                                                        onClick={handleStartNewJob}
                                                                    >
                                                                        <i className="ri-add-line me-1"></i>
                                                                        Start New Job
                                                                    </Button>
                                                                </>
                                                            )}
                                                        </div>
                                                    </TabPane>
                                                </TabContent>
                                            )}
                                        </div>
                                    </Col>

                                    <Col lg={2}>
                                        <div className="d-flex justify-content-between align-items-center mb-3">
                                            <h5 className="fs-14 text-primary mb-0">
                                                <i className="ri-briefcase-4-line align-middle me-2"></i>{" "}
                                                Quick Job Summary
                                            </h5>
                                        </div>
                                        <ul className="list-group mb-3">
                                            <li className="list-group-item d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="my-0">Job Address</h6>
                                                    <small className="text-muted">
                                                        {formatAddress(propertyAddress)}
                                                    </small>
                                                </div>
                                                <div>{isAddressIcon}</div>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between align-items-center bg-light">
                                                <div>
                                                    <h6 className="my-0">Job Categories</h6>
                                                    <CategoryBadges categories={jobCategories} />
                                                </div>
                                                <div>{isCategoryIcon}</div>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between align-items-center bg-light">
                                                <div>
                                                    <h6 className="my-0">Job Title</h6>
                                                    <small>{jobTitle}</small>
                                                </div>
                                                <div>{isCompleteIcon}</div>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="my-0">Job Summary</h6>
                                                    <small className="text-muted">
                                                        {jobSummary}
                                                    </small>
                                                </div>
                                                <div>{isCompleteIcon}</div>
                                            </li>
                                            <li className="list-group-item d-flex justify-content-between align-items-center">
                                                <div>
                                                    <h6 className="my-0">Images / Attachments</h6>
                                                    <small className="text-muted">
                                                        {jobAttachments.length + existingAttachments.length} files selected
                                                    </small>
                                                </div>
                                                <div>{isFilesIcon}</div>
                                            </li>
                                        </ul>

                                        {hasInitialSave && jobId && !isJobSubmitted && (
                                            <div className="alert alert-info">
                                                <small>
                                                    <i className="ri-save-line me-1"></i>
                                                    Job saved as draft
                                                </small>
                                            </div>
                                        )}

                                        {isJobSubmitted && (
                                            <div className="alert alert-success">
                                                <small>
                                                    <i className="ri-check-double-line me-1"></i>
                                                    Job submitted successfully!
                                                </small>
                                            </div>
                                        )}

                                        {/* reCAPTCHA v3 Protection Notice */}
                                        <div className="mt-3 p-2 bg-light rounded">
                                            <div className="text-success text-center" style={{ fontSize: '0.875rem' }}>
                                                🛡️ This form is protected by reCAPTCHA v3
                                            </div>
                                            {process.env.NODE_ENV === 'development' && (
                                                <div className="text-info text-center mt-1" style={{ fontSize: '0.75rem' }}>
                                                    <strong>Dev Mode:</strong> reCAPTCHA v3
                                                    {executeRecaptcha ? ' ✅ Available' : ' ❌ Not Available'}
                                                </div>
                                            )}
                                        </div>
                                    </Col>
                                </Row>
                            </form>
                        </CardBody>
                    </Card>

                    {(activeVerticalTab !== 11 && !isJobSubmitted) && (
                        <div className="d-flex justify-content-end mt-3">
                            <Button
                                type="button"
                                color="success"
                                onClick={handleSubmit}
                                disabled={isLoading || !complete || jobCategories.length === 0 || !propertyAddress.address1}
                                size="lg"
                            >
                                <i className="ri-check-double-line me-1"></i>
                                Submit Job
                            </Button>
                        </div>
                    )}

                    {isJobSubmitted && activeVerticalTab !== 11 && (
                        <div className="d-flex justify-content-end mt-3">
                            <Button
                                type="button"
                                color="primary"
                                onClick={handleStartNewJob}
                                size="lg"
                            >
                                <i className="ri-add-line me-1"></i>
                                Start New Job
                            </Button>
                        </div>
                    )}
                </Col>
            </Row>
        </div>
    );
};

export default CreateJob;